import React, { useState, useEffect, useRef, useCallback } from "react"
import styled, { keyframes } from "styled-components"
import { themes } from "../styles/ColorStyles"
import copy from "copy-to-clipboard"
import publicationsJSON from "../../data/content/publications.json"

const nm = themes.neumorphic
const publications = publicationsJSON.publications

/* ── Media helpers ── */

const getBaseUrl = () => ""

/* Get marker string for a given author name — values are arrays e.g. ["†"] */
const getMarker = (pub, name) => {
  if (!pub.authorMarkers) return null
  const markers = pub.authorMarkers[name]
  return markers && markers.length ? markers.join("") : null
}

/* Render markerLegend as "† Corresponding author · ◯ ..." */
const legendText = pub => {
  if (!pub.markerLegend) return null
  return Object.entries(pub.markerLegend)
    .map(([sym, desc]) => `${sym} ${desc}`)
    .join("  ·  ")
}

const getMediaSrc = src => {
  if (!src) return ""
  if (src.startsWith("http://") || src.startsWith("https://")) return src
  return getBaseUrl() + src
}

/* ── Inline SVG Icons ── */

const IconPaper = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
)

const IconArxiv = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 8v13H3V8"/>
    <rect x="1" y="3" width="22" height="5" rx="1"/>
    <line x1="10" y1="12" x2="14" y2="12"/>
  </svg>
)

const IconCode = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6"/>
    <polyline points="8 6 2 12 8 18"/>
  </svg>
)

const IconGlobe = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
)

const IconVideo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
)

/* Checkmark that draws itself in — same 16px stroke language as the other icons */
const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline className="tick" points="20 6 9 17 4 12" />
  </svg>
)

const IconBibtex = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311C9.591 11.69 11 13.2 11 15c0 1.891-1.567 3.5-3.5 3.5-1.073 0-2.099-.449-2.917-1.179zM14.583 17.321C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311C19.591 11.69 21 13.2 21 15c0 1.891-1.567 3.5-3.5 3.5-1.073 0-2.099-.449-2.917-1.179z"/>
  </svg>
)

const IconDownload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
)

const LINK_TYPES = [
  { key: "paper", icon: IconPaper, label: "Paper", arxivIcon: IconArxiv, arxivLabel: "arXiv" },
  { key: "code", icon: IconCode, label: "Code" },
  { key: "project", icon: IconGlobe, label: "Project" },
  { key: "video", icon: IconVideo, label: "Video" },
  { key: "dataset", icon: IconDownload, label: "Dataset" },
  { key: "dataset_baidu", icon: IconDownload, label: "Dataset (Baidu)" },
  { key: "results", icon: IconDownload, label: "Results" },
]

/* ── SelfE interactive step demo ── */

const SELFE_BASE = "https://xinyu-andy.github.io/SelfE-project/"
const SELFE_STRIP_JS = SELFE_BASE + "strip_data.js"
const SELFE_STEPS = ["2", "4", "8", "50"]

function SelfeDemoStrip() {
  const [data, setData] = useState(null)
  const [step, setStep] = useState("2")
  const scriptRef = useRef(false)

  useEffect(() => {
    // Use cached data if already loaded
    if (typeof window !== "undefined" && window.SELFE_STRIP_DATA) {
      setData(window.SELFE_STRIP_DATA)
      return
    }
    if (scriptRef.current) return
    scriptRef.current = true

    const script = document.createElement("script")
    script.src = SELFE_STRIP_JS
    script.onload = () => setData(window.SELFE_STRIP_DATA)
    script.onerror = () => console.warn("SelfE strip data failed to load")
    document.head.appendChild(script)
  }, [])

  const images = data ? (data[step] || []) : []
  // Fixed image width (240px) + gap (10px) = 250px per item
  // Target speed: 300 px/s → duration = N * 250 / 300
  const IMG_W = 240
  const GAP = 10
  const SPEED = 150 // px/s
  const scrollDuration = images.length * (IMG_W + GAP) / SPEED

  return (
    <DemoWrapper>
      <DemoLabel>Interactive Demo — Inference Steps</DemoLabel>
      <StepButtons>
        {SELFE_STEPS.map(s => (
          <StepBtn key={s} $active={step === s} onClick={() => setStep(s)}>
            {s} steps
          </StepBtn>
        ))}
      </StepButtons>
      {!data ? (
        <DemoLoading>Loading images…</DemoLoading>
      ) : (
        <StripViewport>
          {/* Duplicate images for seamless infinite scroll: animate -50% = one full set */}
          <StripTrack key={step} $duration={scrollDuration}>
            {[...images, ...images].map((img, i) => (
              <StripImg
                key={i}
                src={SELFE_BASE + img.src}
                alt={img.prompt}
                title={img.prompt}
                loading="lazy"
              />
            ))}
          </StripTrack>
        </StripViewport>
      )}
    </DemoWrapper>
  )
}

/* ── PubCard with hover-to-play ── */

function PubCard({ pub, onOpen, isOpen, lazy = false }) {
  const handleBtnClick = useCallback(e => {
    e.stopPropagation()
    onOpen()
  }, [onOpen])

  // Lazy: don't set src while panel is collapsed — avoids loading hidden GIFs
  const teaserSrc = lazy ? undefined : getMediaSrc(pub.teaserGif || pub.teaser)

  return (
    <Card id={pub.id} onClick={onOpen}>
      <TeaserWrap>
        <CardTeaser
          src={teaserSrc}
          alt={pub.title}
          style={lazy ? { background: "#d5dbe3" } : undefined}
        />
      </TeaserWrap>
      <CardContent>
        <CardTitle><a href={`/research/${pub.id}/`} onClick={e => e.stopPropagation()} style={{ color: "inherit", textDecoration: "none" }}>{pub.title}</a></CardTitle>
        <details onClick={e => e.stopPropagation()}>
          <summary>Summary & resources</summary>
          {pub.tldr && <p>{pub.tldr}</p>}
          {Object.entries(pub.links || {}).map(([label, url]) => <a key={label} href={url} style={{ marginRight: 12 }}>{label}</a>)}
          <a href={`/research/${pub.id}/`}>Full details & citation</a>
        </details>
        <AuthorList>
          {pub.authors.map((author, i) => (
            <React.Fragment key={i}>
              {i > 0 && ", "}
              {author === "Tianyu Wang" ? (
                <>
                  <BoldAuthor>{author}</BoldAuthor>
                  {getMarker(pub, author) && (
                    <AuthorMarker>{getMarker(pub, author)}</AuthorMarker>
                  )}
                </>
              ) : (
                <>
                  <span>{author}</span>
                  {getMarker(pub, author) && (
                    <AuthorMarker>{getMarker(pub, author)}</AuthorMarker>
                  )}
                </>
              )}
            </React.Fragment>
          ))}
        </AuthorList>
        {/* markerLegend hidden */}
        <VenueLine>
          <VenueBadge>{pub.venue} {pub.year}</VenueBadge>
          {pub.role && <LeadBadge>{pub.role}</LeadBadge>}
        </VenueLine>
      </CardContent>
      <LiquidGlassBtn
        onClick={handleBtnClick}
        aria-label={isOpen ? "Close details" : "Open details"}
      >
        <LiquidGlassBtnIcon $open={isOpen}>+</LiquidGlassBtnIcon>
      </LiquidGlassBtn>
    </Card>
  )
}

const highlights = publications.filter(p => p.year >= 2025 && p.role)
const others = publications.filter(p => !(p.year >= 2025 && p.role))

export default function PublicationSection() {
  const [selected, setSelected] = useState(null)
  const [copied, setCopied] = useState(false)
  const copyTimer = useRef(null)

  // Reset the copy affordance whenever a different paper is opened/closed
  useEffect(() => {
    setCopied(false)
    clearTimeout(copyTimer.current)
  }, [selected])

  useEffect(() => () => clearTimeout(copyTimer.current), [])

  const handleCopyBibtex = () => {
    if (copied) return
    // copy-to-clipboard falls back to window.prompt() where the clipboard is
    // unavailable, which throws in some embedded browsers. Don't let that kill
    // the handler, and don't claim success when the copy didn't happen.
    let ok = false
    try {
      ok = copy(selected.bibtex) !== false
    } catch (err) {
      ok = false
    }
    if (!ok) return
    setCopied(true)
    copyTimer.current = setTimeout(() => setCopied(false), 1800)
  }
  const [othersOpen, setOthersOpen] = useState(false)

  useEffect(() => {
    if (!selected) return
    const handleEsc = (e) => {
      if (e.key === "Escape") setSelected(null)
    }
    document.addEventListener("keydown", handleEsc)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", handleEsc)
      document.body.style.overflow = ""
    }
  }, [selected])

  return (
    <Wrapper id="publications">
      <SectionHeader>
        <SectionTitle>Publications</SectionTitle>
        <SectionSubtitle>Click a paper to see details</SectionSubtitle>
      </SectionHeader>

      {/* ── Highlights ── */}
      <SubSectionLabel>Selected Works</SubSectionLabel>
      <CardList>
        {highlights.map(pub => (
          <PubCard
            key={pub.id}
            pub={pub}
            onOpen={() => setSelected(pub)}
            isOpen={selected?.id === pub.id}
          />
        ))}
      </CardList>

      {/* ── Other Publications (collapsible) ── */}
      <details onToggle={e => setOthersOpen(e.currentTarget.open)}>
        <OthersToggle as="summary">
          <OthersToggleLabel>More Publications ({others.length})</OthersToggleLabel>
          <ToggleLiquidBtn aria-hidden="true"><LiquidGlassBtnIcon $open={othersOpen}>+</LiquidGlassBtnIcon></ToggleLiquidBtn>
        </OthersToggle>
        <CardList style={{ paddingTop: "8px" }}>
          {others.map(pub => (
            <PubCard
              key={pub.id}
              pub={pub}
              onOpen={() => setSelected(pub)}
              isOpen={selected?.id === pub.id}
              lazy={!othersOpen}
            />
          ))}
        </CardList>
      </details>

      {selected && (
        <ModalOverlay onClick={() => setSelected(null)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <CloseBtn onClick={() => setSelected(null)}>&times;</CloseBtn>

            {selected.teaserVideo ? (
              <ModalTeaserVideo
                src={getMediaSrc(selected.teaserVideo)}
                poster={getMediaSrc(selected.teaser)}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <ModalTeaser src={getMediaSrc(selected.teaser)} alt={selected.title} />
            )}

            <ModalTitle>{selected.title}</ModalTitle>

            <ModalAuthors>
              {selected.authors.map((author, i) => (
                <React.Fragment key={i}>
                  {i > 0 && ", "}
                  {author === "Tianyu Wang" ? (
                    <>
                      <BoldAuthor>{author}</BoldAuthor>
                      {getMarker(selected, author) && (
                        <AuthorMarker>{getMarker(selected, author)}</AuthorMarker>
                      )}
                    </>
                  ) : (
                    <>
                      <span>{author}</span>
                      {getMarker(selected, author) && (
                        <AuthorMarker>{getMarker(selected, author)}</AuthorMarker>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}
              
            </ModalAuthors>
            {/* markerLegend hidden */}

            <ModalVenue>
              <VenueBadge>{selected.venue} {selected.year}</VenueBadge>
              {selected.role && <LeadBadge>{selected.role}</LeadBadge>}
            </ModalVenue>

            {selected.interactiveDemo === "selfe-steps" && (
              <SelfeDemoStrip />
            )}

            {selected.tldr && (
              <ModalSection>
                <ModalLabel>TL;DR</ModalLabel>
                <ModalText>{selected.tldr}</ModalText>
              </ModalSection>
            )}

            {selected.insight && (
              <ModalSection>
                <ModalLabel>Key Insight</ModalLabel>
                <ModalText>{selected.insight}</ModalText>
              </ModalSection>
            )}

            <ActionRow>
              {LINK_TYPES.map(({ key, icon: Icon, label, arxivIcon: ArxivIcon, arxivLabel }) => {
                if (!selected.links[key]) return null
                const url = selected.links[key]
                const isArxiv = key === "paper" && url.includes("arxiv.org")
                const FinalIcon = isArxiv && ArxivIcon ? ArxivIcon : Icon
                const finalLabel = isArxiv && arxivLabel ? arxivLabel : label
                return (
                  <ActionLink
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FinalIcon />
                    {finalLabel}
                  </ActionLink>
                )
              })}
              <CopyBtn
                onClick={handleCopyBibtex}
                $copied={copied}
                aria-live="polite"
              >
                {copied ? <IconCheck /> : <IconBibtex />}
                {copied ? "Copied" : "BibTeX"}
              </CopyBtn>
            </ActionRow>
          </ModalBox>
        </ModalOverlay>
      )}
    </Wrapper>
  )
}

/* ── Animations ── */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const scaleUp = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`

/* ── Section ── */

const Wrapper = styled.div`
  background: ${nm.bg};
  padding: 40px 20px 80px;
`

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 48px;
`

const SectionTitle = styled.h2`
  font-family: bree, sans-serif;
  font-size: 42px;
  font-weight: 700;
  color: ${nm.text1};
  margin: 0 0 8px 0;

  @media (max-width: 550px) {
    font-size: 32px;
  }
`

const SectionSubtitle = styled.p`
  font-family: bree, sans-serif;
  font-size: 18px;
  color: ${nm.text3};
  margin: 0;
`

/* ── Card List ── */

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
`

const Card = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
  padding: 20px;
  border-radius: 20px;
  background: ${nm.bg};
  box-shadow: ${nm.shadow};
  cursor: pointer;
  position: relative;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 10px 10px 20px #b8bec7, -10px -10px 20px #ffffff;
  }

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`

const TeaserWrap = styled.div`
  aspect-ratio: 16 / 9;
  width: 100%;
  overflow: hidden;
`

const cardTeaserStyles = `
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  background: #d5dbe3;
  display: block;
`

const CardTeaser = styled.img`${cardTeaserStyles}`

const CardTeaserVideo = styled.video`${cardTeaserStyles}`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`

const CardTitle = styled.h4`
  font-family: bree, sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: ${nm.text1};
  line-height: 1.4;
  margin: 0;
`

const AuthorList = styled.p`
  font-family: bree, sans-serif;
  font-size: 13px;
  color: ${nm.text2};
  line-height: 1.5;
  margin: 0;
`

const BoldAuthor = styled.span`
  font-weight: 700;
  color: ${nm.text1};
`

/* Superscript marker (†) after author name */
const AuthorMarker = styled.sup`
  font-size: 9px;
  margin-left: 1px;
  color: ${nm.text2};
  opacity: 0.8;
  vertical-align: super;
  line-height: 0;
`

/* Footnote line shown at bottom of author list when markers exist */
const AuthorFootnote = styled.div`
  font-size: 10px;
  color: ${nm.text3};
  margin-top: 2px;
  font-style: italic;
  letter-spacing: 0.01em;
`

const LeadBadge = styled.span`
  font-family: bree, sans-serif;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 8px;
  background: ${nm.accentLight};
  color: ${nm.accent};
`

const VenueLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const VenueBadge = styled.span`
  font-family: bree, sans-serif;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 8px;
  background: ${nm.accentLight};
  color: ${nm.accent};
`

const YearText = styled.span`
  font-family: bree, sans-serif;
  font-size: 13px;
  color: ${nm.text3};
`

/* ── Modal ── */

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  animation: ${fadeIn} 0.25s ease forwards;
  overflow-y: auto;
`

const ModalBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  background: ${nm.bg};
  border-radius: 24px;
  /* Floating dialog sits on a dark scrim, so the neumorphic white highlight
     (shadowLarge) read as a stray halo. Use a plain elevation shadow. */
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28), 0 6px 18px rgba(0, 0, 0, 0.16);
  padding: 40px;
  animation: ${scaleUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;

  @media (max-width: 600px) {
    padding: 24px;
    border-radius: 16px;
  }
`

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: ${nm.bg};
  box-shadow: ${nm.shadowSmall};
  cursor: pointer;
  font-size: 20px;
  color: ${nm.text2};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 1;

  &:hover {
    box-shadow: ${nm.shadowInset};
  }
`

const modalTeaserStyles = `
  width: 100%;
  border-radius: 16px;
  margin-bottom: 24px;
  background: #d5dbe3;
  box-shadow: ${nm.shadowInset};
`

const ModalTeaser = styled.img`${modalTeaserStyles}`

/* Reserve the frame up front: without an intrinsic size a <video> collapses to
   zero height until its poster (or metadata) arrives, leaving a blank gap.
   Every teaser video is 16:9 except omnivcus (5:3), which `contain` letterboxes
   against the same background rather than distorting. */
const ModalTeaserVideo = styled.video`
  ${modalTeaserStyles}
  aspect-ratio: 16 / 9;
  object-fit: contain;
`

const ModalTitle = styled.h3`
  font-family: bree, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: ${nm.text1};
  line-height: 1.3;
  margin: 0 0 12px 0;
`

const ModalAuthors = styled.p`
  font-family: bree, sans-serif;
  font-size: 14px;
  color: ${nm.text2};
  line-height: 1.6;
  margin: 0 0 12px 0;
`

const ModalVenue = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
`

const ModalSection = styled.div`
  margin-bottom: 20px;
`

const ModalLabel = styled.div`
  font-family: bree, sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${nm.accent};
  margin-bottom: 6px;
`

const ModalText = styled.p`
  font-family: bree, sans-serif;
  font-size: 15px;
  line-height: 1.6;
  color: ${nm.text2};
  margin: 0;
`

/* ── Action Buttons ── */

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
`

const actionStyles = `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 50px;
  font-family: bree, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${nm.text2};
  background: ${nm.bg};
  box-shadow: ${nm.shadowSmall};
  border: none;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  svg {
    flex-shrink: 0;
    opacity: 0.6;
    transition: opacity 0.3s ease;
  }

  &:hover {
    box-shadow: inset 4px 4px 8px #b8bec7, inset -4px -4px 8px #ffffff;
    color: ${nm.text1};

    svg {
      opacity: 1;
    }
  }

  &:active {
    box-shadow: inset 4px 4px 8px #b8bec7, inset -4px -4px 8px #ffffff;
  }
`

const ActionLink = styled.a`${actionStyles}`

const ActionBtn = styled.button`${actionStyles}`

/* Copy feedback happens in place, in the site's own vocabulary: the button
   settles into the neumorphic pressed state and the label turns accent, so
   nothing is thrown on top of the page. */
const drawTick = keyframes`
  from { stroke-dashoffset: 24; }
  to   { stroke-dashoffset: 0; }
`

const CopyBtn = styled.button`
  ${actionStyles}

  ${({ $copied }) => $copied && `
    color: ${nm.accent};
    box-shadow: inset 4px 4px 8px #b8bec7, inset -4px -4px 8px #ffffff;
    cursor: default;

    svg { opacity: 1; }
  `}

  .tick {
    stroke-dasharray: 24;
    animation: ${drawTick} 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    .tick { animation: none; stroke-dashoffset: 0; }
  }
`

/* ── Two-section layout ── */

const SubSectionLabel = styled.h3`
  font-family: bree, sans-serif;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.10em;
  color: ${nm.accent};
  max-width: 900px;
  margin: 0 auto 20px;
  padding: 0 4px;
`

const OthersToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 900px;
  margin: 32px auto 0;
  padding: 16px 14px 16px 24px;
  border-radius: 16px;
  background: ${nm.bg};
  box-shadow: ${nm.shadowSmall};
  cursor: pointer;
  overflow: visible;
  transition: box-shadow 0.25s ease, transform 0.2s ease;

  &:hover {
    box-shadow: ${nm.shadow};
    transform: translateY(-1px);
  }

  &:active {
    box-shadow: ${nm.shadowInset};
    transform: translateY(0);
  }
`

const OthersToggleLabel = styled.span`
  font-family: bree, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${nm.text2};
`

/* Inline liquid glass btn for the toggle row (no absolute positioning) */
const ToggleLiquidBtn = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: visible;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(24px) saturate(200%);
  -webkit-backdrop-filter: blur(24px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none; /* clicks handled by parent OthersToggle */
`

const OthersPanel = styled.div`
  overflow: ${({ $open }) => $open ? "visible" : "hidden"};
  max-height: ${({ $open }) => $open ? "9999px" : "0"};
  opacity: ${({ $open }) => $open ? "1" : "0"};
  padding: ${({ $open }) => $open ? "8px 0" : "0"};
  transition:
    max-height 0.5s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.35s ease;
`

/* ── Liquid Glass Button ── */

const LiquidGlassBtn = styled.button`
  position: absolute;
  bottom: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 0;
  cursor: pointer;
  outline: none;
  z-index: 2;

  /* Glass core */
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(24px) saturate(200%);
  -webkit-backdrop-filter: blur(24px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.8);

  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);

  display: flex;
  align-items: center;
  justify-content: center;

  transition: transform 0.15s ease-in-out, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.10);
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.14),
      inset 0 1px 0 rgba(255, 255, 255, 0.95);
  }

  &:active {
    transform: scale(0.94);
  }
`

const LiquidGlassBtnIcon = styled.span`
  display: block;
  font-size: 19px;
  font-weight: 600;
  line-height: 1;
  color: #000;
  user-select: none;
  font-style: normal;
  font-family: system-ui, -apple-system, sans-serif;

  transform: rotate(${({ $open }) => $open ? "45deg" : "0deg"});
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
`

/* ── SelfE Demo Styled Components ── */

const DemoWrapper = styled.div`
  margin: 4px 0 20px 0;
  border-radius: 16px;
  background: ${nm.bg};
  box-shadow: ${nm.shadowInset};
  padding: 20px;
  overflow: hidden;
`

const DemoLabel = styled.div`
  font-family: bree, sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${nm.accent};
  margin-bottom: 12px;
`

const StepButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  margin-bottom: 16px;
`

const StepBtn = styled.button`
  flex: 1;
  min-width: 0;
  padding: 8px clamp(6px, 2vw, 16px);
  border-radius: 50px;
  border: none;
  font-family: bree, sans-serif;
  font-size: clamp(11px, 2.8vw, 13px);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  background: ${nm.bg};
  color: ${({ $active }) => $active ? nm.accent : nm.text2};
  box-shadow: ${({ $active }) => $active ? nm.shadowInset : nm.shadowSmall};

  &:hover {
    color: ${nm.accent};
    box-shadow: ${nm.shadowInset};
  }
`

const DemoLoading = styled.div`
  font-family: bree, sans-serif;
  font-size: 14px;
  color: ${nm.text3};
  text-align: center;
  padding: 24px 0;
`

const marqueeScroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`

const StripViewport = styled.div`
  overflow: hidden;
  width: 100%;
`

const StripTrack = styled.div`
  display: flex;
  gap: 10px;
  width: max-content;
  animation: ${marqueeScroll} ${({ $duration }) => $duration}s linear infinite;

  &:hover {
    animation-play-state: paused;
  }
`

const StripImg = styled.img`
  height: 180px;
  width: 240px;
  flex-shrink: 0;
  border-radius: 10px;
  object-fit: cover;
  background: #d5dbe3;
  box-shadow: 3px 3px 8px #b8bec7, -3px -3px 8px #ffffff;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`
